import React, { useState, useEffect, useRef } from 'react';
import { Icon } from 'antd';
import { IconFont } from '@src/features/common';
import { Parent } from 'parchment/dist/src/blot/abstract/blot';
import Quill, { RangeStatic } from 'quill';
import SFCUploadImg from './SFCUploadImg';
import SFCDivider from './SFCDivider';
import SFCLink from './SFCLink';
import IRange from './types';
type Props = ISFCToolbarProps;

export interface ISFCToolbarProps {
  quill?: Quill;
}

export function SFCToolbar(props: Props) {
  const [toolStyle, setToolStyle] = useState<React.CSSProperties>({});
  const [toolVisiable, setToolVisiable] = useState(false);
  const [barStyle, setBarStyle] = useState<React.CSSProperties>({});
  const [barVisiable, setBarVisiable] = useState(false);
  const toolEl = useRef<HTMLElement>(null);
  const barEl = useRef<HTMLElement>(null);

  const hidebar = () => {
    setBarVisiable(false);
  };

  const hideToolbar = () => {
    setToolVisiable(false);
  };

  useEffect(() => {
    const quill = props.quill;
    const Block = Quill.import('blots/block');

    const editorChange = (
      eventType: string,
      range: IRange | undefined,
      oldRange: RangeStatic,
      source: string
    ) => {
      // 编辑器变化时根据内容一集所选内容修改工具栏的状态
      if (eventType !== 'selection-change' || !range || !quill) {
        return;
      }

      if (range.length === 0) {
        // 如果没有选中任何内容
        setToolVisiable(false);

        const scroll = quill.scroll as Parent;
        const [block, offset] = scroll.descendant(Block, range.index);

        if (block != null && block.domNode.firstChild instanceof HTMLBRElement) {
          // 如果是在一个空行
          const lineBounds = quill.getBounds(range.index);

          setBarVisiable(true);
          setBarStyle({
            top: lineBounds.top + lineBounds.height / 2 + 'px'
          });
        } else {
          hidebar();
          setToolVisiable(false);
        }
      } else {
        // 如果有选中内容
        const rangeStart = quill.getBounds(range.index);
        const rangStartLeft = rangeStart.left;
        const rangStartTop = rangeStart.top;

        hidebar();
        setToolStyle(pre => {
          return {
            ...pre,
            top: rangStartTop - 46 + 'px',
            left: (rangStartLeft < 550 ? rangStartLeft : rangStartLeft - 150) + 'px'
          };
        });
        setToolVisiable(source === 'user');
      }
    };

    if (quill) {
      // 将工具栏元素添加到编辑器中
      toolEl && toolEl.current && quill.addContainer(toolEl.current);
      barEl && barEl.current && quill.addContainer(barEl.current);

      quill.on('editor-change', editorChange);
    }

    return () => {
      if (quill) {
        quill.off('editor-change', editorChange);
      }
    };
  }, [props.quill]);

  return (
    <section id="toolbar-con">
      <section
        id="tooltip-controls"
        className="selected-bar"
        style={{
          ...toolStyle,
          visibility: toolVisiable ? 'visible' : 'hidden'
        }}
        ref={toolEl}
      >
        <button type="button" className="ql-bold button-item selected-bar_first">
          <IconFont type="icon-e-bold" />
          <span className="button-item__tip">
            <p>加粗</p>
            <p>Markdown: **文字** 空格</p>
          </span>
        </button>
        <button type="button" className="ql-italic button-item">
          <IconFont type="icon-e-italic" />
          <span className="button-item__tip">
            <p>斜体</p>
            <p>Markdown: *文字* 空格</p>
          </span>
        </button>
        <button type="button" className="ql-strike button-item">
          <IconFont type="icon-e-strike" />
          <span className="button-item__tip">
            <p>删除线</p>
            <p>Markdown: ~~文字~~ 空格</p>
          </span>
        </button>
        <button type="button" className="ql-underline button-item">
          <IconFont type="icon-e-underline" />
          <span className="button-item__tip">下划线</span>
        </button>
        <span className="tool-bar--line" />
        <button type="button" className="ql-header button-item" value="1">
          <IconFont type="icon-e-t1" />
          <span className="button-item__tip">
            <p>一级标题</p>
            <p>Markdown: # 空格</p>
          </span>
        </button>
        <button type="button" className="ql-header button-item" value="2">
          <IconFont type="icon-e-t2" />
          <span className="button-item__tip">
            <p>二级标题</p>
            <p>Markdown: ## 空格</p>
          </span>
        </button>
        <button type="button" className="ql-header button-item" value="3">
          <IconFont type="icon-e-t3" />
          <span className="button-item__tip">
            <p>三级标题</p>
            <p>Markdown: ### 空格</p>
          </span>
        </button>
        <span className="tool-bar--line" />
        <button className="ql-list button-item" value="ordered" type="button">
          <IconFont type="icon-e-order-list" />
          <span className="button-item__tip">
            <p>有序项目</p>
            <p>Markdown: 1. 空格</p>
          </span>
        </button>
        <button className="ql-list button-item" value="bullet" type="button">
          <IconFont type="icon-e-unorder-list" />
          <span className="button-item__tip">
            <p>无序项目</p>
            <p>Markdown: * 空格</p>
          </span>
        </button>
        <span className="tool-bar--line" />
        <SFCLink quill={props.quill} hideToolbar={hideToolbar} />
        <button type="button" className="ql-blockquote button-item selected-bar_last">
          <IconFont type="icon-e-blockquote" />
          <span className="button-item__tip">
            <p>引用</p>
            <p>Markdown: &gt; 空格</p>
          </span>
        </button>
        <button type="button" className="ql-clean button-item">
          <IconFont type="icon-e-clean" />
          <span className="button-item__tip">
            <p>清除格式</p>
          </span>
        </button>
      </section>
      <section
        id="sidebar-controls"
        className="add-bar"
        style={{
          ...barStyle,
          visibility: barVisiable ? 'visible' : 'hidden'
        }}
        ref={barEl}
      >
        <Icon className="add-bar--icon" type="plus" />
        <div className="add-bar__hide__con">
          <div className="add-bar--hide-part">
            <SFCUploadImg quill={props.quill} />
            <SFCDivider quill={props.quill} />
            <button type="button" className="ql-code-block add-bar--btn" onClick={hidebar}>
              <IconFont type="icon-e-code" />
              <span className="button-item__tip">
                <p>插入代码块</p>
                <p>Markdown: ```</p>
              </span>
            </button>
            <span className="add-bar__line" />
            <button type="button" className="ql-header add-bar--btn" value="1" onClick={hidebar}>
              <IconFont type="icon-e-t1" />
              <span className="button-item__tip">
                <p>一级标题</p>
                <p>Markdown: # 空格</p>
              </span>
            </button>
            <button type="button" className="ql-header add-bar--btn" value="2" onClick={hidebar}>
              <IconFont type="icon-e-t2" />
              <span className="button-item__tip">
                <p>二级标题</p>
                <p>Markdown: ## 空格</p>
              </span>
            </button>
            <button type="button" className="ql-header add-bar--btn" value="3" onClick={hidebar}>
              <IconFont type="icon-e-t3" />
              <span className="button-item__tip">
                <p>三级标题</p>
                <p>Markdown: ### 空格</p>
              </span>
            </button>
            <span className="add-bar__line" />
            <button
              type="button"
              className="ql-list add-bar--btn"
              value="ordered"
              onClick={hidebar}
            >
              <IconFont type="icon-e-order-list" />
              <span className="button-item__tip">
                <p>有序项目</p>
                <p>Markdown: 1. 空格</p>
              </span>
            </button>
            <button type="button" className="ql-list add-bar--btn" value="bullet" onClick={hidebar}>
              <IconFont type="icon-e-unorder-list" />
              <span className="button-item__tip">
                <p>无序项目</p>
                <p>Markdown: * 空格</p>
              </span>
            </button>
            <span className="add-bar__line" />
            <button
              type="button"
              className="ql-blockquote add-bar--btn"
              value="3"
              onClick={hidebar}
            >
              <IconFont type="icon-e-blockquote" />
              <span className="button-item__tip">
                <p>引用</p>
                <p>Markdown: &gt; 空格</p>
              </span>
            </button>
            <button type="button" className="ql-clean add-bar--btn" onClick={hidebar}>
              <IconFont type="icon-e-clean" />
              <span className="button-item__tip">
                <p>清除格式</p>
              </span>
            </button>
          </div>
        </div>
      </section>
    </section>
  );
}

export default SFCToolbar;
